import { useMemo, useState, ReactNode } from "react";
import {
  Box,
  Typography,
  Chip,
  ChipProps,
  Link,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
} from "@mui/material";
import { capitalize, flatten, keyBy } from "lodash";
import StepIndicator from "../../common/StepIndicator";
import { Step } from "../../common/StepIndicator/StepIndicator";
import theme from "../../../theme";
import {
  GetTodayTripsQuery,
  ShipmentEventInput,
  ShipmentEventType,
  ShipmentLocationType,
  Status,
  TripShipmentLocation,
} from "../../../graphql/generated";
import { getEffectiveShipmentIds } from "../../../utils/shipment/getEffectiveShipmentIds";
import { ShipmentLocationDetails } from "../../shipment/ShipmentSummary/ShipmentSummary";
import ShipmentEventLogButton from "../../shipment/ShipmentEventLogButton";
import { Stack } from "@mui/system";
import { formatMileage } from "../../../utils/labels/formatDistance";
import driverLabel from "../../../utils/labels/driverLabel";
import {
  getTripCurrentLocation,
  getTripNextLocation,
} from "../../trip-planning/TripTimeline/TripTimeline";
import LoadingOverlay from "../../common/LoadingOverlay";
import { DateTimePicker } from "@mui/x-date-pickers";
import { useTranslation } from "react-i18next";
import enumLabel from "../../../utils/labels/enumLabel";
import statusKey from "../../../utils/i18n/statusKey";
import {
  buildTripRouteSegments,
  TripForMileage,
  TripRouteEndpointSegment,
  TripRouteSegment,
  TripRouteStopSegment,
} from "../../../utils/mileage";
import { TripForShipmentLocationUtils } from "../../../utils/trip/getEtaWindowStatus";
import { testIds } from "./TripSummary.testIds";

type TripSummaryData = GetTodayTripsQuery["trips"]["data"][0];

export type TripSummaryType = Omit<TripSummaryData, "shipments"> & {
  shipments: Pick<
    TripSummaryData["shipments"][0],
    "_id" | "shipmentNumber" | "events" | "charges"
  >[];
};

export type TripSummaryProps = {
  trip: TripSummaryType;
  routeSegments?: TripRouteSegment[];
  horizontal?: boolean;
  loading?: boolean;
  onClick?: () => void;
  onStart?: (trip: TripSummaryType) => void;
  onShipmentEventLog?: (event: ShipmentEventInput) => void;
  readonly?: boolean;
  singleClickTripCompletionEnabled?: boolean;
};

function renderMileageLabel(
  distanceTo: number | null | undefined,
): ReactNode | undefined {
  if (distanceTo === null || distanceTo === undefined) {
    return undefined;
  }

  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ whiteSpace: "nowrap" }}
    >
      {formatMileage(distanceTo)}
    </Typography>
  );
}

function EndpointStepContent({
  title,
  name,
  addressLabel,
  label,
  centered = false,
  part,
}: {
  title: string;
  name?: string;
  addressLabel?: string;
  label?: string;
  centered?: boolean;
  part?: "above" | "below";
}) {
  const displayName = name || (part ? undefined : label);
  const displayAddress = addressLabel ?? (part ? undefined : undefined);

  if (part === "above") {
    return (
      <Stack
        spacing={0.25}
        alignItems={centered ? "center" : undefined}
        sx={{ width: "100%" }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textAlign: centered ? "center" : undefined }}
        >
          {title}
        </Typography>
        {name ? (
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              textAlign: centered ? "center" : undefined,
            }}
          >
            {name}
          </Typography>
        ) : null}
      </Stack>
    );
  }

  if (part === "below") {
    const belowLabel = addressLabel ?? label;
    return belowLabel ? (
      <Typography
        variant="body2"
        sx={{
          fontWeight: 400,
          fontSize: 13,
          textAlign: centered ? "center" : undefined,
        }}
      >
        {belowLabel}
      </Typography>
    ) : null;
  }

  return (
    <Stack spacing={0.25} alignItems={centered ? "center" : undefined}>
      <Typography variant="caption" color="text.secondary">
        {title}
      </Typography>
      {displayName ? (
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13 }}>
          {displayName}
        </Typography>
      ) : null}
      {displayAddress ? (
        <Typography variant="body2" sx={{ fontWeight: 400, fontSize: 13 }}>
          {displayAddress}
        </Typography>
      ) : null}
    </Stack>
  );
}

const endpointDotColor = theme.palette.text.secondary;

function getOutgoingMileageLabel(
  segmentIndex: number,
  segments: TripRouteSegment[],
): ReactNode | undefined {
  const nextSegment = segments[segmentIndex + 1];
  if (!nextSegment) {
    return undefined;
  }

  return renderMileageLabel(nextSegment.distanceTo);
}

function hasMileageData(trip: TripSummaryType): boolean {
  const tripForMileage = trip as unknown as TripForMileage;
  return (
    trip.shipmentLocations.some(
      (location) =>
        (location as { distanceTo?: number | null }).distanceTo != null,
    ) ||
    tripForMileage.routeStartPoint != null ||
    tripForMileage.routeEndPoint != null
  );
}

const TripSummary = ({
  trip,
  routeSegments: routeSegmentsProp,
  horizontal = false,
  onClick,
  onStart,
  onShipmentEventLog,
  loading,
  readonly,
  singleClickTripCompletionEnabled,
}: TripSummaryProps) => {
  const { t } = useTranslation(["trips", "orders", "common"]);
  const tripForEta = trip as TripForShipmentLocationUtils;
  const getTripStatusLabel = useMemo(
    () => (status: Status) => {
      let color: ChipProps["color"] = "default";
      switch (status) {
        case Status.InProgress:
          color = "lightPrimary";
          break;
        case Status.Complete:
          color = "primary";
          break;
        default:
          break;
      }
      return (
        <Chip
          label={capitalize(
            t("trips:statusLabel", {
              status: statusKey[status],
              defaultValue: enumLabel(status),
            }),
          )}
          color={color}
        />
      );
    },
    [t],
  );

  const resolvedRouteSegments = useMemo(() => {
    if (routeSegmentsProp) {
      return routeSegmentsProp;
    }
    if (!hasMileageData(trip)) {
      return null;
    }
    return buildTripRouteSegments(trip as unknown as TripForMileage);
  }, [routeSegmentsProp, trip]);

  const allShippedGoodsById = keyBy(
    flatten(
      trip.shipmentLocations.map((location) => location.shippedGoods || []),
    ),
    "_id",
  );

  const separatorLength = horizontal ? 48 : 40;

  const nextLocation =
    getTripCurrentLocation(trip) || getTripNextLocation(trip);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [eventDate, setEventDate] = useState(new Date());
  const [eventType, setEventType] = useState<ShipmentEventType | null>(null);
  const [eventLabel, setEventLabel] = useState<string | null>(null);
  const [eventDescription, setEventDescription] = useState<string | null>(null);
  const [location, setLocation] =
    useState<typeof trip.shipmentLocations[0] | null>(null);

  const onShipmentEventLogForLocation = useMemo(
    () =>
      trip.carrier || trip.status === Status.InProgress
        ? (
            location: typeof trip.shipmentLocations[0],
            eventType: ShipmentEventType,
          ) => {
            const shipmentIds = getEffectiveShipmentIds(
              location as Pick<
                TripShipmentLocation,
                "shipmentIds" | "shipmentId"
              >,
            );
            if (shipmentIds.length === 0) {
              return;
            }
            setEventDate(new Date());
            setLocation(location);
            setEventType(eventType);
            setIsModalOpen(true);
          }
        : undefined,
    [trip],
  );

  const steps: Step[] = useMemo(() => {
    const getStopKey = (
      loc: typeof trip.shipmentLocations[0],
    ): string => {
      const stopName = loc.receiver?.name ?? loc.shipper?.name ?? "";
      return (
        loc._id ||
        loc.receiver?._id ||
        loc.shipper?._id ||
        stopName ||
        loc.locationType
      );
    };

    const connectorDataAttributes = (stepKey: string) => ({
      "data-testid": `${testIds.tripSummarySegmentMileage}-${stepKey}`,
    });

    const stopDataAttributes = (
      loc: typeof trip.shipmentLocations[0] | undefined,
    ): Record<string, string> => {
      if (!loc) return {};
      const stopName = loc.receiver?.name ?? loc.shipper?.name ?? "";
      return {
        "data-testid": `${testIds.tripSummaryStop}-${getStopKey(loc)}`,
        "data-stop-role":
          loc.locationType === ShipmentLocationType.Pickup
            ? "shipper"
            : "receiver",
        "data-has-tank": String(
          (loc.receivedGoods || []).some(
            (rg) =>
              !!(rg as { storageFacilityId?: string | null }).storageFacilityId,
          ),
        ),
        "data-stop-name": stopName,
      };
    };

    const stopDetailsDataAttributes = (
      loc: typeof trip.shipmentLocations[0],
    ): Record<string, string> => ({
      ...stopDataAttributes(loc),
      "data-testid": `${testIds.tripStopDetails}-${getStopKey(loc)}`,
    });

    const renderShipmentLocation = (
      shipmentLocation: typeof trip.shipmentLocations[0],
    ) => {
      const eventHandler =
        !trip.carrier && shipmentLocation._id === nextLocation?._id
          ? onShipmentEventLogForLocation?.bind(null, shipmentLocation)
          : undefined;

      const eventLogButton = eventHandler ? (
        <Box sx={{ mt: 1, textAlign: horizontal ? "center" : "left" }}>
          <ShipmentEventLogButton
            shipmentLocation={shipmentLocation}
            onShipmentEventLog={eventHandler}
            disabled={readonly}
          />
        </Box>
      ) : null;

      if (horizontal) {
        return {
          contentAbove: (
            <ShipmentLocationDetails
              allShippedGoodsById={allShippedGoodsById}
              shipmentLocation={shipmentLocation}
              part="above"
              centered
              readonly={readonly}
              trip={tripForEta}
            />
          ),
          contentBelow: (
            <>
              <ShipmentLocationDetails
                allShippedGoodsById={allShippedGoodsById}
                shipmentLocation={shipmentLocation}
                part="below"
                centered
                readonly={readonly}
                trip={tripForEta}
              />
              {eventLogButton}
            </>
          ),
        };
      }

      return {
        content: (
          <Box sx={{ width: "100%" }}>
            <ShipmentLocationDetails
              allShippedGoodsById={allShippedGoodsById}
              shipmentLocation={shipmentLocation}
              readonly={readonly}
              trip={tripForEta}
            />
            {eventLogButton}
          </Box>
        ),
      };
    };

    const makeLocationStep = (
      shipmentLocation: typeof trip.shipmentLocations[0],
      options: {
        hideFirstLine?: boolean;
        hideLastLine?: boolean;
        connectorLabel?: ReactNode;
        color?: string;
      } = {},
    ): Step => ({
      id: shipmentLocation._id || shipmentLocation.tripShipmentLocationId,
      hideFirstLine: options.hideFirstLine,
      hideLastLine: options.hideLastLine,
      ...renderShipmentLocation(shipmentLocation),
      connectorLabel: options.connectorLabel,
      color: options.color ??
        (shipmentLocation.locationType === ShipmentLocationType.Pickup
          ? theme.palette.primary.main
          : theme.palette.accent.main),
      separatorLength,
      dataAttributes: stopDataAttributes(shipmentLocation),
      contentBelowDataAttributes: horizontal
        ? stopDetailsDataAttributes(shipmentLocation)
        : undefined,
    });

    if (!resolvedRouteSegments) {
      return trip.shipmentLocations.map((shipmentLocation, index) =>
        makeLocationStep(shipmentLocation, {
          hideFirstLine: index === 0,
          hideLastLine: index === trip.shipmentLocations.length - 1,
          color:
            shipmentLocation.locationType === ShipmentLocationType.Pickup
              ? theme.palette.primary.main
              : theme.palette.accent.main,
        }),
      );
    }

    const startEndpoint = resolvedRouteSegments.find(
      (segment): segment is TripRouteEndpointSegment =>
        segment.kind === "endpoint" && segment.endpointType === "start",
    );
    const endEndpoint = resolvedRouteSegments.find(
      (segment): segment is TripRouteEndpointSegment =>
        segment.kind === "endpoint" && segment.endpointType === "end",
    );
    const stopSegments = resolvedRouteSegments.filter(
      (segment): segment is TripRouteStopSegment => segment.kind === "stop",
    );

    const getConnectorLabel = (segmentIndex: number) =>
      getOutgoingMileageLabel(segmentIndex, resolvedRouteSegments);
    const getSegmentIndex = (segment: TripRouteSegment) =>
      resolvedRouteSegments.indexOf(segment);

    const routeSteps: Step[] = [];

    if (startEndpoint) {
      routeSteps.push({
        id: "start-endpoint",
        ...(horizontal
          ? {
              contentAbove: (
                <EndpointStepContent
                  title={t("startingPoint", "Starting Point")}
                  name={startEndpoint.endpoint.name}
                  part="above"
                  centered
                />
              ),
              contentBelow: (
                <EndpointStepContent
                  title={t("startingPoint", "Starting Point")}
                  name={startEndpoint.endpoint.name}
                  addressLabel={startEndpoint.endpoint.addressLabel}
                  part="below"
                  centered
                />
              ),
            }
          : {
              content: (
                <EndpointStepContent
                  title={t("startingPoint", "Starting Point")}
                  name={startEndpoint.endpoint.name}
                  addressLabel={startEndpoint.endpoint.addressLabel}
                  label={startEndpoint.label}
                />
              ),
            }),
        connectorLabel: getConnectorLabel(getSegmentIndex(startEndpoint)),
        connectorDataAttributes: connectorDataAttributes("start-endpoint"),
        dataAttributes: {
          "data-testid": testIds.tripSummaryStartPoint,
        },
        contentBelowDataAttributes: horizontal
          ? {
              "data-testid": testIds.tripSummaryStartPointDetails,
              "data-address-label": startEndpoint.endpoint.addressLabel,
            }
          : undefined,
        color: endpointDotColor,
        separatorLength,
      });
    }

    stopSegments.forEach((segment, index) => {
      const { shipmentLocation } = segment;
      const locationParts = renderShipmentLocation(
        shipmentLocation as typeof trip.shipmentLocations[0],
      );
      routeSteps.push({
        id:
          shipmentLocation._id ||
          shipmentLocation.tripShipmentLocationId ||
          `stop-${index}`,
        ...locationParts,
        connectorLabel: getConnectorLabel(getSegmentIndex(segment)),
        connectorDataAttributes: connectorDataAttributes(
          getStopKey(
            shipmentLocation as typeof trip.shipmentLocations[0],
          ),
        ),
        color:
          shipmentLocation.locationType === ShipmentLocationType.Pickup
            ? theme.palette.primary.main
            : theme.palette.accent.main,
        separatorLength,
        hideFirstLine: index === 0 && !startEndpoint,
        hideLastLine: index === stopSegments.length - 1 && !endEndpoint,
        dataAttributes: stopDataAttributes(
          shipmentLocation as typeof trip.shipmentLocations[0],
        ),
        contentBelowDataAttributes: horizontal
          ? stopDetailsDataAttributes(
              shipmentLocation as typeof trip.shipmentLocations[0],
            )
          : undefined,
      });
    });

    if (endEndpoint) {
      routeSteps.push({
        id: "end-endpoint",
        ...(horizontal
          ? {
              contentAbove: (
                <EndpointStepContent
                  title={t("endPoint", "End Point")}
                  name={endEndpoint.endpoint.name}
                  part="above"
                  centered
                />
              ),
              contentBelow: (
                <EndpointStepContent
                  title={t("endPoint", "End Point")}
                  name={endEndpoint.endpoint.name}
                  addressLabel={endEndpoint.endpoint.addressLabel}
                  part="below"
                  centered
                />
              ),
            }
          : {
              content: (
                <EndpointStepContent
                  title={t("endPoint", "End Point")}
                  name={endEndpoint.endpoint.name}
                  addressLabel={endEndpoint.endpoint.addressLabel}
                  label={endEndpoint.label}
                />
              ),
            }),
        color: endpointDotColor,
        dataAttributes: {
          "data-testid": testIds.tripSummaryEndPoint,
        },
        contentBelowDataAttributes: horizontal
          ? {
              "data-testid": testIds.tripSummaryEndPointDetails,
              "data-address-label": endEndpoint.endpoint.addressLabel,
            }
          : undefined,
        separatorLength,
      });
    }

    if (routeSteps.length > 0) {
      routeSteps[0].hideFirstLine = true;
      routeSteps[routeSteps.length - 1].hideLastLine = true;
    }

    return routeSteps;
  }, [
    resolvedRouteSegments,
    trip,
    tripForEta,
    allShippedGoodsById,
    readonly,
    nextLocation,
    onShipmentEventLogForLocation,
    horizontal,
    separatorLength,
    t,
  ]);

  return (
    <Box data-testid={`${testIds.tripSummaryOpenDetails}-${trip._id}`}
      sx={{
        borderRadius: horizontal ? 0 : 5,
        p: horizontal ? 0 : 2,
        backgroundColor: horizontal
          ? "transparent"
          : theme.palette.primaryBackground.main,
        position: "relative",
        cursor: onClick ? "pointer" : undefined,
      }}
      onClick={onClick}
    >
      <LoadingOverlay loading={loading || false} />
      {!horizontal ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: 700,
              fontSize: 13,
            }}
          >
            {trip.driver ? driverLabel(trip.driver) : ""}
          </Typography>
          <Stack
            spacing={2}
            direction="row"
            sx={{
              alignItems: "center",
            }}
          >
            <Link
              data-testid={testIds.tripSummaryOpenTrip}
              href={`/trips/details/${trip._id}`}
              target="_blank"
            >
              {capitalize(t("trip", "Trip"))} {trip.tripNumber}
            </Link>
            {getTripStatusLabel(trip.status)}
          </Stack>
        </Box>
      ) : null}
      <Box
        sx={
          horizontal
            ? {
                overflowX: "auto",
                width: "100%",
                pb: 1,
              }
            : undefined
        }
      >
        <Box sx={horizontal ? { minWidth: "100%", width: "100%" } : undefined}>
          <StepIndicator
            steps={steps}
            horizontal={horizontal}
            fullWidth={horizontal}
          />
        </Box>
      </Box>
      {!trip.carrier &&
      ![Status.InProgress, Status.Complete, Status.Deleted].includes(
        trip.status,
      ) &&
      onStart ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: horizontal ? 1 : 0,
          }}
        >
          <Button
            data-testid={testIds.tripSummaryStartTrip}
            variant="outlined"
            onClick={(event) => {
              event.stopPropagation();
              onStart(trip);
            }}
            disabled={readonly}
          >
            {t("trips:start", "Start trip")}
          </Button>
        </Box>
      ) : onShipmentEventLog ? (
        <Stack
          direction="row"
          spacing={2}
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: horizontal ? 1 : 0,
          }}
        >
          <Button
            data-testid={testIds.tripSummaryLogCheckCall}
            variant="outlined"
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onShipmentEventLogForLocation?.(
                nextLocation as typeof trip.shipmentLocations[0],
                ShipmentEventType.CheckCall,
              );
            }}
            disabled={readonly}
          >
            {t("trips:checkCall", "Check Call")}
          </Button>
          {singleClickTripCompletionEnabled &&
          trip.status !== Status.Complete ? (
            <Button
              data-testid={testIds.tripSummaryCompleteTrip}
              variant="outlined"
              onClick={(event) => {
                event.stopPropagation();
                onShipmentEventLogForLocation?.(
                  nextLocation as typeof trip.shipmentLocations[0],
                  ShipmentEventType.TripEnd,
                );
              }}
              disabled={readonly}
              size="small"
            >
              {t("trips:completeTrip", "Complete Trip")}
            </Button>
          ) : null}
        </Stack>
      ) : null}
      <Dialog
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
      >
        <DialogTitle>
          {t("trips:selectedEventDate", "Select the event date")}
        </DialogTitle>
        <DialogContent>
          <Stack sx={{ mt: 2 }} spacing={2}>
            <DateTimePicker
              ampm={false}
              label={capitalize(t("trips:event.date", "Event date"))}
              value={eventDate}
              onChange={(date) => {
                if (!date) {
                  return;
                }
                setEventDate(date);
              }}
              renderInput={(params) => <TextField {...params} />}
            />
            {eventType === ShipmentEventType.CheckCall ? (
              <>
                <TextField
                  size="small"
                  label={capitalize(t("trips:event.one", "Event"))}
                  name="eventLabel"
                  value={eventLabel}
                  onChange={(event) => {
                    setEventLabel(event.target.value);
                  }}
                  inputProps={
                    {
                      "data-testid": testIds.tripSummaryEvent,
                    } as React.InputHTMLAttributes<HTMLInputElement>
                  }
                />
                <TextField
                  size="small"
                  label={capitalize(t("trips:event.notes", "Notes"))}
                  name="eventDescription"
                  multiline
                  minRows={3}
                  maxRows={10}
                  value={eventDescription}
                  onChange={(event) => {
                    setEventDescription(event.target.value);
                  }}
                  inputProps={
                    {
                      "data-testid": testIds.tripSummaryNotes,
                    } as React.InputHTMLAttributes<HTMLInputElement>
                  }
                />
              </>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            data-testid={testIds.tripSummaryCancel}
            onClick={() => {
              setIsModalOpen(false);
            }}
          >
            {t("common:cancel", "Cancel")}
          </Button>
          <Button
            data-testid={testIds.tripSummarySave}
            onClick={() => {
              const shipmentIds = getEffectiveShipmentIds(
                location as Pick<
                  TripShipmentLocation,
                  "shipmentIds" | "shipmentId"
                > | null,
              );
              if (shipmentIds.length === 0 || !location) {
                return;
              }
              if (!eventType) {
                return;
              }
              setIsModalOpen(false);
              // Use the first shipment ID for multi-shipment locations; the
              // backend applies the event to all shipments at the location.
              onShipmentEventLog?.({
                type: eventType,
                label: eventLabel,
                description: eventDescription,
                trip: trip._id,
                shipment: shipmentIds[0],
                shipmentLocation: location._id,
                tripShipmentLocation: location.tripShipmentLocationId,
                location: {
                  latitude: 0,
                  longitude: 0,
                },
                date: eventDate,
              });
              setEventLabel(null);
              setEventDescription(null);
            }}
          >
            {t("common:save", "Save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TripSummary;
